import { create } from 'zustand';
import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import type { User, UserRole, Permission } from '@twg/shared';
import { readAuthRejection } from './authRejection';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Allowed email domains for authentication
const ALLOWED_DOMAINS = [
  'thewrightgardner.com',
  'onpointgen.com',
  'cubinvestments.com',
  'twgplants.com',
];

// Bootstrap super admin emails - allows login even without DB record
// Once logged in, the database isSuperAdmin flag takes precedence
// Configured via VITE_BOOTSTRAP_SUPER_ADMIN_EMAILS env var (comma-separated)
const BOOTSTRAP_SUPER_ADMIN_EMAILS: string[] = (
  import.meta.env.VITE_BOOTSTRAP_SUPER_ADMIN_EMAILS || ''
).split(',').map((e: string) => e.trim().toLowerCase()).filter(Boolean);

const isAllowedEmail = (email: string): boolean => {
  const lowerEmail = email.toLowerCase();
  return ALLOWED_DOMAINS.some(domain => lowerEmail.endsWith(`@${domain}`)) ||
         BOOTSTRAP_SUPER_ADMIN_EMAILS.includes(lowerEmail);
};

const isBootstrapSuperAdmin = (email: string): boolean => {
  return BOOTSTRAP_SUPER_ADMIN_EMAILS.includes(email.toLowerCase());
};

interface AuthState {
  user: Partial<User> | null;
  firebaseUser: FirebaseUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isSuperAdmin: boolean;
  error: string | null;
  loginWithGoogle: () => Promise<boolean>;
  loginAsTestUser: (role?: UserRole) => void;
  logout: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasPermission: (permission: Permission) => boolean;
  getPrimaryRole: () => UserRole | null;
  getIdToken: () => Promise<string | null>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  firebaseUser: null,
  isLoading: false,
  isAuthenticated: false,
  isInitialized: false,
  isSuperAdmin: false,
  error: null,

  // Test-only login for E2E tests - only works in development/test mode
  loginAsTestUser: (role: UserRole = 'Ops') => {
    if (import.meta.env.PROD) {
      console.warn('loginAsTestUser is only available in development/test mode');
      return;
    }
    // E2E fixture only (blocked in production above). Deliberately broader than the real table in
    // @twg/shared (Ops here also gets warehouse + reporting; Holiday gets two field permissions) so the
    // existing E2E screens render; a real Ops user does not have these. Do not copy it as a rule.
    const rolePermissions: Record<UserRole, Permission[]> = {
      SuperAdmin: [], // SuperAdmin flag grants all permissions via hasPermission check
      Admin: ['management_views', 'workorder_manager', 'warehouse', 'scheduling', 'reporting', 'billing', 'qa_reports', 'user_management'],
      Ops: ['management_views', 'workorder_manager', 'warehouse', 'scheduling', 'reporting', 'qa_reports'],
      Tech: ['tech_dashboard', 'service_logging', 'workorder_view'],
      Holiday: ['tech_dashboard', 'service_logging'],
    };
    const testUser: Partial<User> = {
      id: 'test-user-id',
      email: 'test@thewrightgardner.com',
      name: 'E2E Test User',
      roles: [role],
      permissions: rolePermissions[role] || [],
      employeeId: '1',
      viewTags: ['management', 'workorder-manager', 'warehouse', 'qa'],
    };
    set({
      user: testUser,
      firebaseUser: null,
      isAuthenticated: true,
      isInitialized: true,
      isLoading: false,
      isSuperAdmin: role === 'SuperAdmin',
      error: null,
    });
  },

  loginWithGoogle: async () => {
    set({ isLoading: true, error: null });

    try {
      const credential = await signInWithPopup(auth, googleProvider);
      const firebaseUser = credential.user;
      const email = firebaseUser.email?.toLowerCase();

      if (!email) {
        await signOut(auth);
        set({ isLoading: false, error: 'No email found in Google account' });
        return false;
      }

      // Verify email is allowed (domain whitelist or bootstrap super admin)
      if (!isAllowedEmail(email)) {
        await signOut(auth);
        set({ isLoading: false, error: 'Your email domain is not authorized. Contact your administrator.' });
        return false;
      }

      // Get Firebase ID token for API authentication
      const idToken = await firebaseUser.getIdToken();

      // Try to fetch user data from the new users/me endpoint
      let userData = null;
      let rejection: string | null = null;
      try {
        const response = await fetch(`${API_URL}/api/v1/users/me`, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        });
        if (response.ok) {
          const result = await response.json();
          userData = result.data;
        } else {
          rejection = await readAuthRejection(response);
        }
      } catch (apiError) {
        console.warn('Users API not available:', apiError);
        // API might not be deployed - continue for bootstrap super admins
      }

      // The API refused this sign-in as untrusted: end it here, before the employee
      // fallback or the bootstrap-super-admin shortcut can let it through.
      if (rejection) {
        await signOut(auth);
        set({ isLoading: false, error: rejection });
        return false;
      }

      // If we got user data from the API, use it
      if (userData) {
        const user: Partial<User> = {
          id: firebaseUser.uid,
          email: email,
          name: userData.name,
          roles: userData.roles || [userData.role],
          permissions: userData.effectivePermissions || [],
          employeeId: userData.id,
          viewTags: userData.viewTags || [],
        };

        set({
          user,
          firebaseUser,
          isAuthenticated: true,
          isLoading: false,
          isSuperAdmin: userData.isSuperAdmin || false,
          error: null,
        });

        return true;
      }

      // Fallback: Try legacy employee endpoint
      let employee = null;
      try {
        const response = await fetch(
          `${API_URL}/api/v1/employees/by-email/${encodeURIComponent(email)}`,
          {
            headers: {
              'Authorization': `Bearer ${idToken}`,
            },
          }
        );
        if (response.ok) {
          const result = await response.json();
          employee = result.data;
        }
      } catch (apiError) {
        console.warn('Employee API not available:', apiError);
      }

      // Bootstrap super admins can sign in even without any API/DB record
      if (isBootstrapSuperAdmin(email)) {
        const user: Partial<User> = {
          id: firebaseUser.uid,
          email: email,
          name: employee?.name || firebaseUser.displayName || email.split('@')[0],
          roles: ['SuperAdmin'],
          permissions: [], // SuperAdmin flag grants all permissions
          employeeId: employee?.id || null,
          viewTags: employee?.viewTags || ['management', 'workorder-manager', 'warehouse', 'qa'],
        };

        set({
          user,
          firebaseUser,
          isAuthenticated: true,
          isLoading: false,
          isSuperAdmin: true,
          error: null,
        });

        return true;
      }

      // Non-super-admins require employee record
      if (!employee) {
        await signOut(auth);
        set({ isLoading: false, error: 'Your account is not set up in the system. Contact your manager.' });
        return false;
      }

      if (!employee.isActive) {
        await signOut(auth);
        set({ isLoading: false, error: 'Your account is inactive. Contact your manager.' });
        return false;
      }

      const user: Partial<User> = {
        id: firebaseUser.uid,
        email: email,
        name: employee.name,
        roles: [employee.role as UserRole],
        permissions: [],
        employeeId: employee.id,
        viewTags: employee.viewTags || [],
      };

      set({
        user,
        firebaseUser,
        isAuthenticated: true,
        isLoading: false,
        isSuperAdmin: employee.isSuperAdmin || false,
        error: null,
      });

      return true;
    } catch (error: unknown) {
      console.error('Login failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Login failed';

      // Handle specific Firebase errors
      if (errorMessage.includes('popup-closed-by-user')) {
        set({ isLoading: false, error: null }); // Don't show error for cancelled login
      } else if (errorMessage.includes('account-exists')) {
        set({ isLoading: false, error: 'Account exists with different credentials' });
      } else {
        set({ isLoading: false, error: 'Login failed. Please try again.' });
      }
      return false;
    }
  },

  logout: async () => {
    await signOut(auth);
    set({
      user: null,
      firebaseUser: null,
      isAuthenticated: false,
      isSuperAdmin: false,
      error: null,
    });
  },

  hasRole: (role: UserRole) => {
    const { user } = get();
    return user?.roles?.includes(role) ?? false;
  },

  hasPermission: (permission: Permission) => {
    const { user, isSuperAdmin } = get();
    // SuperAdmin has all permissions
    if (isSuperAdmin) return true;
    return user?.permissions?.includes(permission) ?? false;
  },

  getPrimaryRole: () => {
    const { user } = get();
    if (!user?.roles?.length) return null;
    if (user.roles.includes('SuperAdmin')) return 'SuperAdmin';
    if (user.roles.includes('Admin')) return 'Admin';
    if (user.roles.includes('Ops')) return 'Ops';
    if (user.roles.includes('Tech')) return 'Tech';
    if (user.roles.includes('Holiday')) return 'Holiday';
    return null;
  },

  getIdToken: async () => {
    const { firebaseUser } = get();
    if (!firebaseUser) return null;
    return firebaseUser.getIdToken();
  },

  clearError: () => set({ error: null }),
}));

// Initialize auth state listener (skip in test mode to allow test auth injection)
// Test mode is detected by checking if __testAuth will be exposed (non-production builds)
const isTestMode = !import.meta.env.PROD;

/** Restores the session when Firebase reports a signed-in (or signed-out) user. Exported for tests. */
export async function handleAuthStateChange(firebaseUser: FirebaseUser | null): Promise<void> {
  if (firebaseUser) {
    const email = firebaseUser.email?.toLowerCase();

    if (!email || !isAllowedEmail(email)) {
      await signOut(auth);
      useAuthStore.setState({
        user: null,
        firebaseUser: null,
        isAuthenticated: false,
        isSuperAdmin: false,
        isInitialized: true,
      });
      return;
    }

    // Get Firebase ID token for API authentication
    const idToken = await firebaseUser.getIdToken();

    // Try to fetch user data from the new users/me endpoint
    let userData = null;
    let rejection: string | null = null;
    try {
      const response = await fetch(`${API_URL}/api/v1/users/me`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });
      if (response.ok) {
        const result = await response.json();
        userData = result.data;
      } else {
        rejection = await readAuthRejection(response);
      }
    } catch (apiError) {
      console.warn('Users API not available:', apiError);
    }

    // The API refused this sign-in as untrusted: sign out and say why, before the
    // employee fallback or the bootstrap-super-admin shortcut can restore it.
    if (rejection) {
      await signOut(auth);
      useAuthStore.setState({
        user: null,
        firebaseUser: null,
        isAuthenticated: false,
        isSuperAdmin: false,
        isInitialized: true,
        error: rejection,
      });
      return;
    }

    // If we got user data from the API, use it
    if (userData) {
      const user: Partial<User> = {
        id: firebaseUser.uid,
        email: email,
        name: userData.name,
        roles: userData.roles || [userData.role],
        permissions: userData.effectivePermissions || [],
        employeeId: userData.id,
        viewTags: userData.viewTags || [],
      };

      useAuthStore.setState({
        user,
        firebaseUser,
        isAuthenticated: true,
        isSuperAdmin: userData.isSuperAdmin || false,
        isInitialized: true,
      });
      return;
    }

    // Fallback: Try legacy employee endpoint
    let employee = null;
    try {
      const response = await fetch(
        `${API_URL}/api/v1/employees/by-email/${encodeURIComponent(email)}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        }
      );
      if (response.ok) {
        const result = await response.json();
        employee = result.data;
      }
    } catch (apiError) {
      console.warn('Employee API not available:', apiError);
    }

    // Bootstrap super admins can sign in even without any API/DB record
    if (isBootstrapSuperAdmin(email)) {
      const user: Partial<User> = {
        id: firebaseUser.uid,
        email: email,
        name: employee?.name || firebaseUser.displayName || email.split('@')[0],
        roles: ['SuperAdmin'],
        permissions: [],
        employeeId: employee?.id || null,
        viewTags: employee?.viewTags || ['management', 'workorder-manager', 'warehouse', 'qa'],
      };

      useAuthStore.setState({
        user,
        firebaseUser,
        isAuthenticated: true,
        isSuperAdmin: true,
        isInitialized: true,
      });
      return;
    }

    // Non-super-admins require employee record
    if (!employee) {
      await signOut(auth);
      useAuthStore.setState({
        user: null,
        firebaseUser: null,
        isAuthenticated: false,
        isSuperAdmin: false,
        isInitialized: true,
      });
      return;
    }

    if (!employee.isActive) {
      await signOut(auth);
      useAuthStore.setState({
        user: null,
        firebaseUser: null,
        isAuthenticated: false,
        isSuperAdmin: false,
        isInitialized: true,
      });
      return;
    }

    const user: Partial<User> = {
      id: firebaseUser.uid,
      email: email,
      name: employee.name,
      roles: [employee.role as UserRole],
      permissions: [],
      employeeId: employee.id,
      viewTags: employee.viewTags || [],
    };

    useAuthStore.setState({
      user,
      firebaseUser,
      isAuthenticated: true,
      isSuperAdmin: employee.isSuperAdmin || false,
      isInitialized: true,
    });
  } else {
    useAuthStore.setState({
      user: null,
      firebaseUser: null,
      isAuthenticated: false,
      isSuperAdmin: false,
      isInitialized: true,
    });
  }
}

if (!isTestMode) {
  onAuthStateChanged(auth, handleAuthStateChange);
} else {
  // In test mode, immediately set isInitialized to allow test auth injection
  useAuthStore.setState({
    isInitialized: true,
    isAuthenticated: false,
  });
}
